import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star, Heart, MapPin, Wifi, Car, Waves as Pool } from 'lucide-react';
import { useFavorites } from '../contexts/FavoritesContext';

const PropertyList = () => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    location: searchParams.get('location') || '',
    type: searchParams.get('type') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    guests: searchParams.get('guests') || '',
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || ''
  });

  const fetchProperties = async (currentFilters) => {
    setLoading(true);
    try {
      const { getPublicProperties } = require('../api/properties');
      const data = await getPublicProperties(currentFilters);
      if (data && Array.isArray(data)) {
        const formattedProperties = data.map(p => ({
          id: p.id,
          title: p.name,
          location: p.city ? `${p.city}, ${p.country || ''}` : p.address,
          price: p.pricePerNight,
          rating: p.rating || 0,
          reviews: Array.isArray(p.reviews) ? p.reviews.length : 0,
          image: (p.images && p.images.length > 0) ? p.images[0] : "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=300&fit=crop",
          guests: p.maxGuests,
          bedrooms: p.bedrooms,
          bathrooms: p.bathrooms,
          amenities: [] // Can map real amenities later if needed
        }));
        setProperties(formattedProperties);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties(filters);
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handlePriceRangeChange = (e) => {
    const val = e.target.value;
    if (val === 'Price Range' || !val) {
      setFilters({ ...filters, minPrice: '', maxPrice: '' });
      return;
    }

    // Handle "₦300,000+" format
    if (val.includes('+')) {
      const min = val.replace(/[₦, +]/g, '');
      setFilters({ ...filters, minPrice: min, maxPrice: '' });
      return;
    }

    // Handle "₦50,000 - ₦150,000" format
    const parts = val.replace(/[₦,]/g, '').split(' - ');
    if (parts.length === 2) {
      setFilters({ ...filters, minPrice: parts[0].trim(), maxPrice: parts[1].trim() });
    }
  };

  const handleSearch = () => {
    fetchProperties(filters);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Explore Properties</h1>
          <p className="text-gray-600">Discover your perfect getaway from our curated collection of luxury rentals</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                name="location"
                value={filters.location}
                onChange={handleFilterChange}
                placeholder="Search destinations..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            {/* <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 mb-1">CHECK-IN</label>
              <input
                type="date"
                name="checkIn"
                value={filters.checkIn}
                onChange={handleFilterChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm text-gray-900"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-600 mb-1">CHECK-OUT</label>
              <input
                type="date"
                name="checkOut"
                value={filters.checkOut}
                min={filters.checkIn}
                onChange={handleFilterChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm text-gray-900"
              />
            </div> */}
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="">Property Type</option>
              <option value="villa">Villa</option>
              <option value="apartment">Apartment</option>
              <option value="house">House</option>
              <option value="cabin">Cabin</option>
            </select>
            <select
              onChange={handlePriceRangeChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option>Price Range</option>
              <option>₦0 - ₦50,000</option>
              <option>₦50,000 - ₦150,000</option>
              <option>₦150,000 - ₦300,000</option>
              <option>₦300,000+</option>
            </select>
            <button
              onClick={handleSearch}
              className="px-6 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white font-semibold rounded-lg hover:from-pink-600 hover:to-red-600 transition-all duration-200 self-end"
            >
              Search
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <svg className="animate-spin h-8 w-8 text-pink-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-4 text-gray-600">Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-600">No properties found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <div key={property.id} className="property-card bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                {/* Property Image */}
                <div className="relative">
                  <Link to={`/property/${property.id}`}>
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-48 object-cover"
                    />
                  </Link>
                  <button
                    onClick={() => toggleFavorite(property)}
                    className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
                  >
                    <Heart
                      className={`w-4 h-4 transition-colors ${isFavorite(property.id) ? "text-red-500 fill-red-500" : "text-gray-600"
                        }`}
                    />
                  </button>
                </div>

                {/* Property Content */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-1" />
                      {property.location}
                    </div>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-400 mr-1" />
                      <span className="text-sm font-semibold">{property.rating}</span>
                      <span className="text-sm text-gray-600 ml-1">({property.reviews})</span>
                    </div>
                  </div>

                  <Link to={`/property/${property.id}`}>
                    <h3 className="font-semibold text-gray-900 mb-2 hover:text-pink-600 transition-colors">
                      {property.title}
                    </h3>
                  </Link>

                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <span>{property.guests} guests</span>
                    <span className="mx-2">•</span>
                    <span>{property.bedrooms} bedrooms</span>
                    <span className="mx-2">•</span>
                    <span>{property.bathrooms} bathrooms</span>
                  </div>

                  {/* Amenities */}
                  <div className="flex items-center space-x-3 mb-3">
                    {property.amenities.map((AmenityIcon, index) => (
                      AmenityIcon && typeof AmenityIcon === 'function' ? (
                        <AmenityIcon key={index} className="w-4 h-4 text-blue-500" />
                      ) : null
                    ))}
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">₦{property.price?.toLocaleString()}</span>
                      <span className="text-gray-600 ml-1">/ night</span>
                    </div>
                    <Link
                      to={`/property/${property.id}`}
                      className="px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white text-sm font-semibold rounded-lg hover:from-pink-600 hover:to-red-600 transition-all duration-200"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        <div className="text-center mt-12">
          <button className="px-8 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200">
            Load More Properties
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyList;
